'use client'

import { useState } from "react";
import Image from "next/image";
import { motion } from "framer-motion";

import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import { getImageUrl } from "@/shared/utils/image";

const galleryImages = [
  { src: "/studio-collection/Copy of IJT_0608.JPG", alt: "Raya family portrait in studio" },
  { src: "/studio-collection/KDTX0002.jpg", alt: "Raya portrait with clean studio lighting" },
  { src: "/studio-collection/KDTX0013.jpg", alt: "Minimal family portrait for Raya" },
  { src: "/studio-collection/KDTX0019.jpg", alt: "Studio family pose during Raya session" },
  { src: "/talent-collection-1/KDTX0080.jpg", alt: "Warm Raya portrait with refined styling" },
  { src: "/talent-collection-1/KDTX0060.jpg", alt: "Simple family portrait for festive season" },
  { src: "/talent-collection-1/KDTX0035.jpg", alt: "Portrait session with soft warm expression" },
  { src: "/talent-collection-2/KDTX0244.jpg", alt: "Raya portrait with balanced modern finish" },
  { src: "/talent-collection-2/KDTX0324.jpg", alt: "Studio portrait with polished festive tone" },
  { src: "/talent-collection-2/KDTX0265.jpg", alt: "Family portrait ready for sharing and print" },
  { src: "/talent-collection-2/KDTX0294.jpg", alt: "Warm lifestyle-inspired Raya portrait" },
  { src: "/talent-collection-3/Studio Session-037.jpg", alt: "Raya portrait with modern studio framing" },
  { src: "/talent-collection-3/KDTX0129.jpg", alt: "Timeless portrait with calm expression" },
  { src: "/talent-collection-3/KDTX0157.jpg", alt: "Minimal portrait with festive elegance" },
  { src: "/talent-collection-3/KDTX0173.jpg", alt: "Studio portrait with soft natural presence" },
  { src: "/talent-collection-3/KDTX0217.jpg", alt: "Clean family portrait composition for Raya" },
];

export const RayaGallerySection = () => {
  const [selectedImage, setSelectedImage] = useState<(typeof galleryImages)[number] | null>(null);
  const [failedImages, setFailedImages] = useState<Record<string, boolean>>({});
  const [loadedImages, setLoadedImages] = useState<Record<string, boolean>>({});

  const markFailed = (src: string) => {
    setFailedImages((current) => (current[src] ? current : { ...current, [src]: true }));
  };

  const markLoaded = (src: string) => {
    setLoadedImages((current) => (current[src] ? current : { ...current, [src]: true }));
  };

  const previewImage = selectedImage && !failedImages[selectedImage.src] ? selectedImage : null;

  return (
    <>
      <section id="gallery" className="px-6 py-16 lg:px-12 lg:py-24">
        <div className="mx-auto max-w-7xl">
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
            {galleryImages.map((image, index) => {
              const hasFailed = Boolean(failedImages[image.src]);
              const isLoaded = Boolean(loadedImages[image.src]);

              return (
                <motion.button
                  key={image.src}
                  type="button"
                  initial={{ opacity: 0, y: 24 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, amount: 0.12 }}
                  transition={{ duration: 0.55, delay: index * 0.04 }}
                  className="group relative aspect-[4/5] overflow-hidden rounded-[1.6rem] bg-stone-900 text-left focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-stone-200/80"
                  onClick={() => setSelectedImage(image)}
                  aria-label={`Preview ${image.alt}`}
                >
                  {!hasFailed ? (
                    <>
                      <Image
                        src={getImageUrl(image.src)}
                        alt={image.alt}
                        fill
                        className="object-cover transition-transform duration-700 group-hover:scale-[1.06]"
                        sizes="(max-width: 768px) 100vw, 25vw"
                        onLoad={() => markLoaded(image.src)}
                        onError={() => markFailed(image.src)}
                      />
                      {!isLoaded ? (
                        <div className="absolute inset-0 animate-pulse bg-stone-800/80" />
                      ) : null}
                      <div className="absolute inset-0 bg-[linear-gradient(180deg,rgba(0,0,0,0.04)_0%,rgba(0,0,0,0.18)_45%,rgba(0,0,0,0.6)_100%)] opacity-70 transition-opacity duration-500 group-hover:opacity-100" />
                    </>
                  ) : (
                    <div className="flex h-full w-full items-center justify-center bg-stone-900 px-6 text-center">
                      <div>
                        <p className="text-xs uppercase tracking-[0.3em] text-stone-500">
                          Preview unavailable
                        </p>
                        <p className="mt-3 text-sm leading-6 text-stone-300">
                          {image.alt}
                        </p>
                      </div>
                    </div>
                  )}
                </motion.button>
              );
            })}
          </div>
        </div>
      </section>

      <Dialog
        open={Boolean(selectedImage)}
        onOpenChange={(open) => {
          if (!open) {
            setSelectedImage(null);
          }
        }}
      >
        {selectedImage ? (
          <DialogContent
            showCloseButton={false}
            className="max-w-5xl overflow-hidden rounded-[2rem] border-stone-800 bg-stone-950 p-0 text-stone-50 shadow-[0_24px_100px_rgba(0,0,0,0.45)]"
          >
            <DialogTitle className="sr-only">{selectedImage.alt}</DialogTitle>
            <button
              type="button"
              onClick={() => setSelectedImage(null)}
              className="absolute right-4 top-4 z-10 rounded-full bg-stone-900/90 px-3 py-2 text-xs uppercase tracking-[0.2em] text-stone-100 transition hover:bg-stone-800"
            >
              Close
            </button>
            <div className="relative aspect-4/3 w-full bg-stone-900">
              {previewImage ? (
                <>
                  <Image
                    src={getImageUrl(previewImage.src)}
                    alt={previewImage.alt}
                    fill
                    className="object-contain"
                    sizes="100vw"
                    priority
                    onLoad={() => markLoaded(previewImage.src)}
                    onError={() => markFailed(previewImage.src)}
                  />
                  {!loadedImages[previewImage.src] ? (
                    <div className="absolute inset-0 animate-pulse bg-stone-800/80" />
                  ) : null}
                </>
              ) : (
                <div className="flex h-full w-full items-center justify-center px-6 text-center">
                  <div>
                    <p className="text-xs uppercase tracking-[0.3em] text-stone-500">
                      Preview unavailable
                    </p>
                    <p className="mt-3 text-sm leading-6 text-stone-300">
                      {selectedImage.alt}
                    </p>
                  </div>
                </div>
              )}
            </div>
          </DialogContent>
        ) : null}
      </Dialog>
    </>
  );
};
